import { useCallback, useEffect, useState } from "react";
import { buildTooltipPosition } from "./ctooltip-selectors";
import type { CtooltipPlacement, TooltipPosition } from "./Ctooltip.types";

interface UseTooltipPositionInput {
  anchorElement: HTMLElement | null;
  isOpen: boolean;
  placement: CtooltipPlacement;
  width?: number;
}

export function useTooltipPosition({
  anchorElement,
  isOpen,
  placement,
  width,
}: UseTooltipPositionInput): TooltipPosition | null {
  const [position, setPosition] = useState<TooltipPosition | null>(null);

  const updatePosition = useCallback((): void => {
    if (!anchorElement || !anchorElement.isConnected) {
      setPosition(null);
      return;
    }

    setPosition(
      buildTooltipPosition({
        anchorRect: anchorElement.getBoundingClientRect(),
        placement,
        viewportWidth: document.documentElement.clientWidth,
        width,
      }),
    );
  }, [anchorElement, placement, width]);

  useEffect(() => {
    if (!isOpen) {
      const frameId = window.requestAnimationFrame(() => {
        setPosition(null);
      });

      return () => {
        window.cancelAnimationFrame(frameId);
      };
    }

    const frameId = window.requestAnimationFrame(updatePosition);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen, updatePosition]);

  return position;
}
